import { useEffect, useMemo } from 'react';
import { money } from '../lib/format';

export type CelebrationKind = 'day' | 'payday' | 'milestone' | 'shield' | 'repair' | 'restored' | 'house';

interface Props {
  kind: CelebrationKind;
  name: string;
  streak: number;
  amount: number;
  currency: string;
  paydayLabel: string;
  /** Milestone reached, or the date a shield covered. */
  detail?: string | number;
  onClose: () => void;
}

const SCENES: Record<
  Exclude<CelebrationKind, 'day' | 'payday'>,
  { emoji: string; title: string; sub: (p: Props) => string }
> = {
  milestone: {
    emoji: '🏅',
    title: 'Milestone!',
    sub: (p) => `${p.detail} days in a row. That is a proper habit now, ${p.name}.`,
  },
  shield: {
    emoji: '🛡️',
    title: 'Shield used',
    sub: (p) => `Yesterday went sideways, so a shield covered it. Your ${p.streak}-day streak is safe.`,
  },
  repair: {
    emoji: '🩹',
    title: 'Streak repaired',
    sub: (p) => `You went back and finished it. ${p.streak} days and counting.`,
  },
  restored: {
    emoji: '🤝',
    title: 'Streak restored',
    sub: (p) => `Mum and Dad gave you that day back. ${p.streak} days — keep it going.`,
  },
  house: {
    emoji: '🏠',
    title: 'House streak!',
    sub: (p) => `${p.detail} days where everyone finished. Time for ${p.name}.`,
  },
};

const COLOURS = ['#ffd166', '#4ade80', '#22d3ee', '#f472b6', '#a78bfa', '#fb923c'];

/** Full-screen moment for finishing the day, or unlocking the week's money. */
export default function Celebration(props: Props): JSX.Element {
  const { kind, name, streak, amount, currency, paydayLabel, onClose } = props;
  useEffect(() => {
    const timer = setTimeout(onClose, kind === 'payday' ? 4200 : 3200);
    return () => clearTimeout(timer);
  }, [kind, onClose]);

  // Confetti is generated once per mount so re-renders don't reshuffle it mid-fall.
  const pieces = useMemo(
    () =>
      Array.from({ length: kind === 'payday' || kind === 'house' ? 46 : 36 }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * 0.9,
        duration: 2.2 + Math.random() * 1.6,
        rot: `${Math.round(Math.random() * 720 - 360)}deg`,
        colour: COLOURS[i % COLOURS.length],
        coin: (kind === 'payday' || kind === 'milestone') && i % 3 === 0,
      })),
    [kind],
  );

  return (
    <div className="q-cele" onClick={onClose} role="presentation">
      {pieces.map((p) => (
        <span
          key={p.id}
          className={`q-confetti${p.coin ? ' coin' : ''}`}
          style={{
            left: `${p.left}%`,
            background: p.coin ? 'transparent' : p.colour,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
            ['--rot' as string]: p.rot,
          }}
        >
          {p.coin ? '🪙' : ''}
        </span>
      ))}
      <div className="q-cele-card">
        {kind !== 'payday' && kind !== 'day' ? (
          <>
            <span className="q-cele-emoji">{SCENES[kind].emoji}</span>
            <div className="q-cele-title">{SCENES[kind].title}</div>
            <div className="q-cele-sub">{SCENES[kind].sub(props)}</div>
          </>
        ) : kind === 'payday' ? (
          <>
            <span className="q-cele-emoji">💰</span>
            <div className="q-cele-title">Payday unlocked!</div>
            <div className="q-cele-sub">
              {paydayLabel === 'today'
                ? `Nice one, ${name}. It's payday today!`
                : `Nice one, ${name}. Keep it up until ${paydayLabel} and it's yours.`}
            </div>
            <span className="q-cele-amount">{money(amount, currency)}</span>
          </>
        ) : (
          <>
            <span className="q-cele-emoji">🎉</span>
            <div className="q-cele-title">All done today!</div>
            <div className="q-cele-sub">
              {streak > 1 ? `🔥 ${streak} day streak — keep it going.` : '🔥 Streak started. Same again tomorrow?'}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
